import type { BrandType } from 'noddi-types';
import { brands } from 'noddi-types';
import { brandZoneHost } from 'noddi-util';

import { environment, isLocal } from '../constants/environment';

export enum AppType {
  Admin = 'admin',
  Customer = 'customer',
  Partner = 'partner',
  Facility = 'facility'
}

const localHosts: Record<AppType, string> = {
  [AppType.Customer]: 'http://localhost:3000',
  [AppType.Admin]: 'http://localhost:3002',
  [AppType.Partner]: 'http://localhost:3004',
  [AppType.Facility]: 'http://localhost:3005'
};

const getLocalHost = (appType: AppType): string => localHosts[appType];

export const toWhitelabelBrand = (slug: string | null | undefined): BrandType | undefined => {
  if (slug != null && slug in brands && slug !== brands.noddi) {
    return slug as BrandType;
  }
  return undefined;
};

const getDomain = (appType: AppType, brand?: BrandType): string => {
  if (appType === AppType.Customer) {
    return brandZoneHost(brand ?? brands.noddi);
  }
  return brandZoneHost(appType);
};

const getNoddiBasePathFromEnvironment = (environment: string, appType: AppType, brand?: BrandType): string => {
  const domain = getDomain(appType, brand);

  switch (environment) {
    case 'local':
      return getLocalHost(appType);
    case 'development':
      return `https://dev.${domain}`;
    case 'production':
      return `https://${domain}`;
    default:
      throw new Error(`Invalid environment: ${environment}`);
  }
};

// Backend-generated customer URLs (e.g. booking.pageUrl) always point at the deployed
// customer domain. In local dev, rewrite the host to the local customer app so links open
// against localhost instead of the dev/prod deployment. Path, query, and hash are preserved.
export const toLocalCustomerUrl = (url: string): string => {
  if (!isLocal) {
    return url;
  }
  try {
    const parsed = new URL(url);
    const local = new URL(getLocalHost(AppType.Customer));
    parsed.protocol = local.protocol;
    parsed.host = local.host;
    return parsed.toString();
  } catch {
    return url;
  }
};

export const getExternalAppUrl = ({
  externalApp,
  route,
  brand
}: {
  externalApp: AppType;
  route: string;
  brand?: BrandType;
}) => {
  const basePath = getNoddiBasePathFromEnvironment(environment, externalApp, brand);
  return `${basePath}${route}`;
};
